/* Orangatang Page — <ow-page> (wave 26.08 Task 12). Orangatang Wheels ONLY.
   The section root on every OW page; the buy-module behaviour from Task 7's ow-wheel-guide.js:
   per-card durometer / bearings pick + add to cart, riding-style filters and lineup dimming. On a page
   with no wheel cards render() is a no-op (no listeners, no subscriptions).
   No fetches: every variant's price block is server-rendered (hidden) and the variant map is an inline
   JSON script per card; add-to-cart goes through the theme's <cart-provider> via CART_ADD (leaf
   components never POST to the cart), so the cart drawer opens as it would from any product card.
   Cards, filter buttons and lineup items only have to be DESCENDANTS of <ow-page>; they may live in
   different bands. Registered in snippets/editor-scripts.liquid for the theme editor. */
const { Core, Utils, Events } = Global;

const SEL = {
  card: '[data-element="card"]',
  duro: '[data-ow-duro]',
  bear: '[data-ow-bear]',
  atc: '[data-ow-atc]',
  filter: '[data-ow-filter]',
  lineup: '[data-ow-lineup]',
  price: '[data-ow-price]',
  priceNone: '[data-ow-price-none]',
  more: '[data-ow-more]',
  atcText: '[data-ow-atc-text]',
  delta: '[data-ow-delta]',
  variants: '[data-ow-variants]'
};

customElements.define('ow-page', class extends Core {
  propTypes = {
    'added-text': String
  }

  render() {
    this._pending = null;
    this._cards = Array.from(this.querySelectorAll(SEL.card)).filter(c => c.querySelector(SEL.variants));
    if (!this._cards.length) return;
    this._cards.forEach(card => {
      card._ow = this._readCard(card);
      this._syncCard(card);
    });
    this.$({ click: this._onClick });
    this.sub(Events.CART_UPDATE, this._onCartUpdate, { global: true });
    this.sub(Events.CART_ERROR, this._onCartError, { global: true });
  }

  destroy() {
    (this._cards || []).forEach(card => { clearTimeout(card._owTimer); delete card._ow; });
    if (this._pending) Utils.$loading(this._pending.btn, false);
    this._cards = null;
    this._pending = null;
  }

  _lineupItem(key) {
    const esc = window.CSS && CSS.escape ? CSS.escape(key) : String(key).replace(/"/g, '\\"');
    return this.querySelector(`[data-ow-lineup="${esc}"]`);
  }

  _readCard(card) {
    const node = card.querySelector(SEL.variants);
    let data = { d: -1, b: -1, v: [] };
    try {
      if (node) data = JSON.parse(node.textContent);
    } catch (e) {
      console.error('[ow-page] variant map unreadable', e);
    }
    return data;
  }

  _match(card, duro, bear) {
    const { d, b, v } = card._ow;
    return v.find(x => (d < 0 || x.o[d] === duro) && (b < 0 || x.o[b] === bear)) || null;
  }

  _syncCard(card) {
    const duro = card.dataset.duro || '';
    const bear = card.dataset.bear || '';
    const { d } = card._ow;
    const variant = this._match(card, duro, bear);
    const strings = window.variantStrings || {};

    card.querySelectorAll(SEL.duro).forEach(btn => {
      const val = btn.getAttribute('data-ow-duro');
      const m = this._match(card, val, bear);
      btn.setAttribute('aria-pressed', val === duro ? 'true' : 'false');
      Utils.$state(btn, 'unavailable', !m);
      Utils.$state(btn, 'sold-out', !!m && !m.a);
    });

    const cheapest = Math.min(...card._ow.v.filter(x => d < 0 || x.o[d] === duro).map(x => x.p));
    card.querySelectorAll(SEL.bear).forEach(btn => {
      const val = btn.getAttribute('data-ow-bear');
      const m = this._match(card, duro, val);
      btn.setAttribute('aria-pressed', val === bear ? 'true' : 'false');
      Utils.$state(btn, 'unavailable', !m);
      Utils.$state(btn, 'sold-out', !!m && !m.a);
      const deltaEl = btn.querySelector(SEL.delta);
      if (deltaEl) {
        const delta = m && isFinite(cheapest) ? m.p - cheapest : 0;
        deltaEl.textContent = delta > 0 && window.Shopify && Shopify.formatMoney
          ? `+${Shopify.formatMoney(delta)}`
          : '';
      }
    });

    card.querySelectorAll(SEL.price).forEach(el => {
      el.hidden = !variant || String(variant.id) !== el.getAttribute('data-ow-price');
    });
    const none = card.querySelector(SEL.priceNone);
    if (none) none.hidden = !!variant;

    const btn = card.querySelector(SEL.atc);
    if (btn) {
      const text = btn.querySelector(SEL.atcText) || btn;
      if (!variant) {
        btn.disabled = true;
        text.textContent = strings.unavailable || 'Unavailable';
      } else if (!variant.a) {
        btn.disabled = true;
        text.textContent = strings.soldOut || 'Sold out';
      } else {
        btn.disabled = false;
        text.textContent = strings.addToCart || 'Add to cart';
      }
    }

    card.dataset.variant = variant ? String(variant.id) : '';

    // the drawing follows the durometer colourway (last word of the option value: "77a Blue" -> blue)
    const color = (duro.trim().split(/\s+/).pop() || '').toLowerCase();
    if (color) {
      card.dataset.color = color;
      const item = this._lineupItem(card.dataset.key);
      if (item) item.dataset.color = color;
    }

    const more = card.querySelector(SEL.more);
    if (more && variant) {
      more.href = `${more.getAttribute('data-ow-more')}?variant=${variant.id}`;
    }
  }

  _onClick(e) {
    const duro = e.target.closest(SEL.duro);
    if (duro) {
      const card = duro.closest(SEL.card);
      card.dataset.duro = duro.getAttribute('data-ow-duro');
      this._clearAdded(card);
      this._syncCard(card);
      return;
    }
    const bear = e.target.closest(SEL.bear);
    if (bear) {
      const card = bear.closest(SEL.card);
      card.dataset.bear = bear.getAttribute('data-ow-bear');
      this._clearAdded(card);
      this._syncCard(card);
      return;
    }
    const atc = e.target.closest(SEL.atc);
    if (atc) {
      e.preventDefault();
      this._addToCart(atc.closest(SEL.card), atc);
      return;
    }
    const filter = e.target.closest(SEL.filter);
    if (filter) {
      this._filter(filter.getAttribute('data-ow-filter'));
    }
  }

  _addToCart(card, btn) {
    const id = Number(card.dataset.variant);
    if (!id || btn.disabled || this._pending) return;
    this._pending = { card, btn };
    Utils.$loading(btn, true);
    this.pub(Events.CART_ADD, { items: [{ id, quantity: 1 }] });
  }

  _onCartUpdate() {
    if (!this._pending) return;
    const { card, btn } = this._pending;
    this._pending = null;
    Utils.$loading(btn, false);
    Utils.$state(card, 'added', true);
    const text = btn.querySelector(SEL.atcText);
    if (text) text.textContent = this.prop('added-text') || 'Added to cart';
    clearTimeout(card._owTimer);
    card._owTimer = setTimeout(() => {
      Utils.$state(card, 'added', false);
      this._syncCard(card);
    }, 2600);
  }

  _onCartError() {
    if (!this._pending) return;
    const { btn } = this._pending;
    this._pending = null;
    Utils.$loading(btn, false);
  }

  _clearAdded(card) {
    clearTimeout(card._owTimer);
    Utils.$state(card, 'added', false);
  }

  _filter(key) {
    this.querySelectorAll(SEL.filter).forEach(b => {
      b.setAttribute('aria-pressed', b.getAttribute('data-ow-filter') === key ? 'true' : 'false');
    });
    this._cards.forEach(card => {
      const tags = (card.dataset.tags || '').split(/[\s,]+/).filter(Boolean);
      const show = key === 'all' || tags.includes(key);
      card.hidden = !show;
      const item = this._lineupItem(card.dataset.key);
      if (item) Utils.$state(item, 'dim', !show);
    });
  }
});
